import * as React from "react";
import { cn } from "lib/utils";

/**
 * PageShell — column-flex container that fills the route's viewport slot
 * (provided by `.right-side` + `.children` in App.css, which are themselves
 * flex columns). Sticky `<PageHeader>` + scrollable `<PageBody>` so individual
 * screens never need `max-h-[Xvh]` on inner regions — that's what created the
 * dual-scroll bug where the outer page and the inner table competed for
 * wheel events.
 *
 * Layout contract:
 *   <PageShell>
 *     <PageHeader>  ...title + global actions...     </PageHeader>
 *     <PageBody>    ...filters, table, pagination... </PageBody>
 *   </PageShell>
 *
 * `<PageBody>` owns the single y-scroll region. If a screen has a very wide
 * table that must own its own internal scroll (rare), pass `noScroll` and
 * give the inner table `flex-1 min-h-0 overflow-auto`.
 *
 * The negative margin trick neutralises the legacy `.children` 10/20/20/10
 * padding so the shell genuinely spans edge-to-edge — `<PageHeader>` /
 * `<PageBody>` then own their own padding (consistent across screens).
 */
const PageShell = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-1 min-h-0 flex-col bg-background",
      "-mt-[10px] -mr-[20px] -mb-[20px] -ml-[10px]",
      className
    )}
    {...props}
  />
));
PageShell.displayName = "PageShell";

const PageHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      "sticky top-0 z-20 flex flex-col gap-1 border-b bg-background/95 px-6 py-5 backdrop-blur supports-[backdrop-filter]:bg-background/80",
      className
    )}
    {...props}
  />
));
PageHeader.displayName = "PageHeader";

const PageHeaderRow = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-wrap items-start justify-between gap-4", className)}
    {...props}
  />
));
PageHeaderRow.displayName = "PageHeaderRow";

const PageTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn("text-2xl font-semibold tracking-tight text-foreground", className)}
    {...props}
  />
));
PageTitle.displayName = "PageTitle";

const PageDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
PageDescription.displayName = "PageDescription";

const PageBody = React.forwardRef(({ className, noScroll = false, ...props }, ref) => (
  <main
    ref={ref}
    className={cn(
      "flex-1 min-h-0 px-6 py-6",
      noScroll ? "flex flex-col overflow-hidden" : "overflow-y-auto",
      className
    )}
    {...props}
  />
));
PageBody.displayName = "PageBody";

export { PageShell, PageHeader, PageHeaderRow, PageTitle, PageDescription, PageBody };
